import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { currencySymbol } from '@/utils/currency';

// Extend jsPDF type to include autoTable
declare module 'jspdf' {
    interface jsPDF {
        autoTable: (options: any) => jsPDF;
    }
}

export interface OrderData {
    _id: string;
    hashedUniqueId: string;
    orderId: string;
    customerName?: string;
    guestName?: string;
    attendeeName?: string;
    customerEmail?: string;
    guestEmail?: string;
    email?: string;
    customerPhone?: string;
    guestPhone?: string;
    phone?: string;
    totalAmount: number;
    currency?: string;
    status: string;
    createdAt: string;
    tickets: Array<{
        ticketId: {
            _id: string;
            name: string;
            price: number;
        };
        quantity: number;
        qrCode: string;
        selectedDate?: string;
        attendeeName?: string;
        attendeeEmail?: string;
        attendeePhone?: string;
    }>;
}

export interface OrderRow {
    name: string;
    email: string;
    phone: string;
    tickets: string;
    showAmount: boolean;
}

export function getOrderRows(order: OrderData, emptyValue: string): OrderRow[] {
    const orderName = order.customerName || order.guestName || order.attendeeName || emptyValue;
    const orderEmail = order.customerEmail || order.guestEmail || order.email || emptyValue;
    const orderPhone = order.customerPhone || order.guestPhone || order.phone || '';
    const tickets = order.tickets || [];
    const hasInfo = (t: OrderData['tickets'][number]) => t.attendeeName || t.attendeeEmail || t.attendeePhone;
    const withInfo = tickets.filter(hasInfo);
    const withoutInfo = tickets.filter(t => !hasInfo(t));

    const rows: Omit<OrderRow, 'showAmount'>[] = [];

    if (withoutInfo.length > 0 || tickets.length === 0) {
        const counts = new Map<string, number>();
        withoutInfo.forEach(t => {
            const name = t.ticketId?.name || 'Unknown Ticket';
            counts.set(name, (counts.get(name) || 0) + (Number(t.quantity) || 1));
        });
        const summary = Array.from(counts.entries()).map(([name, count]) => `${count}x ${name}`).join(', ');
        rows.push({ name: orderName, email: orderEmail, phone: orderPhone, tickets: summary || emptyValue });
    }

    withInfo.forEach(t => {
        rows.push({
            name: t.attendeeName || orderName,
            email: t.attendeeEmail || orderEmail,
            phone: t.attendeePhone || orderPhone,
            tickets: `${Number(t.quantity) || 1}x ${t.ticketId?.name || 'Unknown Ticket'}`
        });
    });

    return rows.map((row, i) => ({ ...row, showAmount: i === 0 }));
}

export interface EventData {
    title: string;
    currency?: string;
    venue?: string;
    address?: string;
    times?: Array<{
        startDate: string;
        startTime?: string;
        endDate: string;
        endTime?: string;
    }>;
}

/**
 * Exports orders and attendees data to PDF
 * @param orders - Array of order data
 * @param eventData - Event information
 * @param filename - Optional custom filename
 */
export function exportOrdersToPDF(
    orders: OrderData[],
    eventData: EventData,
    filename?: string
): void {
    try {
        // Create new PDF document
        const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation for better table layout

        // Set up colors
        const primaryColor = '#2196F3';
        const secondaryColor = '#F5F5F5';
        const textColor = '#333333';
        const lightGray = '#F8F9FA';

        let yPosition = 20;

        // Add header
        doc.setFontSize(20);
        doc.setTextColor(primaryColor);
        doc.text('Event Orders & Attendees Report', 20, yPosition);
        yPosition += 10;

        // Add event information
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text(`Event: ${eventData.title}`, 20, yPosition);
        yPosition += 6;

        if (eventData.venue) {
            doc.text(`Venue: ${eventData.venue}`, 20, yPosition);
            yPosition += 6;
        }

        if (eventData.address) {
            doc.text(`Address: ${eventData.address}`, 20, yPosition);
            yPosition += 6;
        }

        if (eventData.times && eventData.times.length > 0) {
            const eventTime = eventData.times[0];
            const startDate = new Date(eventTime.startDate).toLocaleDateString();
            const timeInfo = eventTime.startTime ? ` at ${eventTime.startTime}` : '';
            doc.text(`Date: ${startDate}${timeInfo}`, 20, yPosition);
            yPosition += 6;
        }

        doc.text(`Generated: ${new Date().toLocaleString()}`, 20, yPosition);
        yPosition += 6;

        doc.text(`Total Orders: ${orders.length}`, 20, yPosition);
        yPosition += 15;

        // Calculate total attendees
        const totalAttendees = orders.reduce((sum, order) => {
            return sum + order.tickets.reduce((ticketSum, ticket) => {
                const quantity = typeof ticket.quantity === 'number'
                    ? ticket.quantity
                    : parseInt(ticket.quantity || '0') || 0;
                return ticketSum + quantity;
            }, 0);
        }, 0);

        doc.text(`Total Attendees: ${totalAttendees}`, 20, yPosition);
        yPosition += 15;

        // Prepare orders data for table
        const ordersTableData = orders.flatMap((order, index) => {
            const orderDate = new Date(order.createdAt).toLocaleDateString();
            return getOrderRows(order, 'N/A').map(row => [
                index + 1,
                row.name,
                row.email,
                row.phone || 'N/A',
                order.hashedUniqueId || order.orderId,
                orderDate,
                row.tickets,
                row.showAmount ? `${currencySymbol(order.currency || eventData.currency)}${order.totalAmount.toFixed(2)}` : '',
                order.status
            ]);
        });

        // Add orders table
        autoTable(doc, {
            startY: yPosition,
            head: [['#', 'Name', 'Email', 'Phone', 'Order ID', 'Date', 'Tickets', 'Amount', 'Status']],
            body: ordersTableData,
            theme: 'grid',
            headStyles: {
                fillColor: primaryColor,
                textColor: 255,
                fontStyle: 'bold',
                fontSize: 10
            },
            bodyStyles: {
                fontSize: 9,
                textColor: textColor
            },
            alternateRowStyles: {
                fillColor: lightGray
            },
            columnStyles: {
                0: { cellWidth: 15 }, // #
                1: { cellWidth: 35 }, // Name
                2: { cellWidth: 40 }, // Email
                3: { cellWidth: 30 }, // Phone
                4: { cellWidth: 35 }, // Order ID
                5: { cellWidth: 25 }, // Date
                6: { cellWidth: 50 }, // Tickets
                7: { cellWidth: 20 }, // Amount
                8: { cellWidth: 25 }  // Status
            },
            margin: { left: 20, right: 20 },
            didDrawPage: (data) => {
                // Add page numbers
                const pageCount = doc.getNumberOfPages();
                doc.setFontSize(8);
                doc.setTextColor(150);
                doc.text(
                    `Page ${data.pageNumber} of ${pageCount}`,
                    doc.internal.pageSize.width - 30,
                    doc.internal.pageSize.height - 10
                );
            }
        });

        // Get the final Y position after the table
        const finalY = (doc as any).lastAutoTable?.finalY || yPosition + 100;

        // Add detailed ticket breakdown if there's space
        if (finalY < 200) {
            yPosition = finalY + 20;

            // Add detailed tickets section
            doc.setFontSize(14);
            doc.setTextColor(primaryColor);
            doc.text('Detailed Ticket Breakdown', 20, yPosition);
            yPosition += 10;

            // Prepare detailed tickets data
            const detailedTicketsData: any[] = [];
            orders.forEach((order, orderIndex) => {
                const attendeeName = order.customerName || order.guestName || order.attendeeName || 'N/A';
                order.tickets.forEach((ticket, ticketIndex) => {
                    // Safely handle price conversion
                    const ticketPrice = typeof ticket.ticketId?.price === 'number'
                        ? ticket.ticketId.price
                        : parseFloat(ticket.ticketId?.price || '0') || 0;

                    const ticketQuantity = typeof ticket.quantity === 'number'
                        ? ticket.quantity
                        : parseInt(ticket.quantity || '0') || 0;

                    detailedTicketsData.push([
                        orderIndex + 1,
                        attendeeName,
                        ticket.ticketId?.name || 'Unknown Ticket',
                        ticketQuantity,
                        `${currencySymbol(order.currency || eventData.currency)}${ticketPrice.toFixed(2)}`,
                        `${currencySymbol(order.currency || eventData.currency)}${(ticketPrice * ticketQuantity).toFixed(2)}`,
                        ticket.qrCode || 'N/A',
                        ticket.selectedDate ? new Date(ticket.selectedDate).toLocaleDateString() : 'N/A'
                    ]);
                });
            });

            // Add detailed tickets table
            autoTable(doc, {
                startY: yPosition,
                head: [['Order #', 'Attendee', 'Ticket Type', 'Qty', 'Unit Price', 'Total', 'QR Code', 'Date']],
                body: detailedTicketsData,
                theme: 'grid',
                headStyles: {
                    fillColor: primaryColor,
                    textColor: 255,
                    fontStyle: 'bold',
                    fontSize: 9
                },
                bodyStyles: {
                    fontSize: 8,
                    textColor: textColor
                },
                alternateRowStyles: {
                    fillColor: lightGray
                },
                columnStyles: {
                    0: { cellWidth: 20 }, // Order #
                    1: { cellWidth: 30 }, // Attendee
                    2: { cellWidth: 35 }, // Ticket Type
                    3: { cellWidth: 15 }, // Qty
                    4: { cellWidth: 20 }, // Unit Price
                    5: { cellWidth: 20 }, // Total
                    6: { cellWidth: 30 }, // QR Code
                    7: { cellWidth: 25 }  // Date
                },
                margin: { left: 20, right: 20 }
            });
        }

        // Generate filename if not provided
        const defaultFilename = `event-orders-${eventData.title.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
        const finalFilename = filename || defaultFilename;

        // Save the PDF
        doc.save(finalFilename);

    } catch (error) {
        console.error('Error generating PDF:', error);
        throw new Error('Failed to generate PDF export');
    }
}

/**
 * Exports a summary report of orders and attendees
 * @param orders - Array of order data
 * @param eventData - Event information
 * @param filename - Optional custom filename
 */
export function exportOrdersSummaryToPDF(
    orders: OrderData[],
    eventData: EventData,
    filename?: string
): void {
    try {
        // Create new PDF document
        const doc = new jsPDF('p', 'mm', 'a4'); // Portrait orientation for summary

        // Set up colors
        const primaryColor = '#2196F3';
        const textColor = '#333333';
        const lightGray = '#F8F9FA';

        let yPosition = 20;

        // Add header
        doc.setFontSize(20);
        doc.setTextColor(primaryColor);
        doc.text('Event Summary Report', 20, yPosition);
        yPosition += 15;

        // Add event information
        doc.setFontSize(12);
        doc.setTextColor(textColor);
        doc.text(`Event: ${eventData.title}`, 20, yPosition);
        yPosition += 8;

        if (eventData.venue) {
            doc.text(`Venue: ${eventData.venue}`, 20, yPosition);
            yPosition += 8;
        }

        if (eventData.address) {
            doc.text(`Address: ${eventData.address}`, 20, yPosition);
            yPosition += 8;
        }

        if (eventData.times && eventData.times.length > 0) {
            const eventTime = eventData.times[0];
            const startDate = new Date(eventTime.startDate).toLocaleDateString();
            const timeInfo = eventTime.startTime ? ` at ${eventTime.startTime}` : '';
            doc.text(`Date: ${startDate}${timeInfo}`, 20, yPosition);
            yPosition += 8;
        }

        doc.text(`Generated: ${new Date().toLocaleString()}`, 20, yPosition);
        yPosition += 20;

        // Calculate summary statistics
        const totalOrders = orders.length;
        const totalAttendees = orders.reduce((sum, order) => {
            return sum + order.tickets.reduce((ticketSum, ticket) => {
                const quantity = typeof ticket.quantity === 'number'
                    ? ticket.quantity
                    : parseInt(ticket.quantity || '0') || 0;
                return ticketSum + quantity;
            }, 0);
        }, 0);
        const totalRevenue = orders.reduce((sum, order) => sum + order.totalAmount, 0);

        // Status breakdown
        const statusBreakdown = orders.reduce((acc, order) => {
            acc[order.status] = (acc[order.status] || 0) + 1;
            return acc;
        }, {} as Record<string, number>);

        // Ticket type breakdown
        const ticketTypeBreakdown = orders.reduce((acc, order) => {
            order.tickets.forEach(ticket => {
                const ticketName = ticket.ticketId?.name || 'Unknown Ticket';
                const quantity = typeof ticket.quantity === 'number'
                    ? ticket.quantity
                    : parseInt(ticket.quantity || '0') || 0;
                acc[ticketName] = (acc[ticketName] || 0) + quantity;
            });
            return acc;
        }, {} as Record<string, number>);

        // Add summary statistics
        doc.setFontSize(14);
        doc.setTextColor(primaryColor);
        doc.text('Summary Statistics', 20, yPosition);
        yPosition += 10;

        doc.setFontSize(11);
        doc.setTextColor(textColor);
        doc.text(`Total Orders: ${totalOrders}`, 20, yPosition);
        yPosition += 6;
        doc.text(`Total Attendees: ${totalAttendees}`, 20, yPosition);
        yPosition += 6;
        doc.text(`Total Revenue: ${currencySymbol(eventData.currency)}${totalRevenue.toFixed(2)}`, 20, yPosition);
        yPosition += 15;

        // Add status breakdown table
        doc.setFontSize(12);
        doc.setTextColor(primaryColor);
        doc.text('Order Status Breakdown', 20, yPosition);
        yPosition += 10;

        const statusData = Object.entries(statusBreakdown).map(([status, count]) => [
            status,
            count.toString(),
            `${((count / totalOrders) * 100).toFixed(1)}%`
        ]);

        autoTable(doc, {
            startY: yPosition,
            head: [['Status', 'Count', 'Percentage']],
            body: statusData,
            theme: 'grid',
            headStyles: {
                fillColor: primaryColor,
                textColor: 255,
                fontStyle: 'bold'
            },
            bodyStyles: {
                textColor: textColor
            },
            alternateRowStyles: {
                fillColor: lightGray
            },
            margin: { left: 20, right: 20 }
        });

        // Add ticket type breakdown
        const finalY = (doc as any).lastAutoTable?.finalY || yPosition + 50;
        yPosition = finalY + 20;

        doc.setFontSize(12);
        doc.setTextColor(primaryColor);
        doc.text('Ticket Type Breakdown', 20, yPosition);
        yPosition += 10;

        const ticketData = Object.entries(ticketTypeBreakdown).map(([ticketType, count]) => [
            ticketType,
            count.toString(),
            `${((count / totalAttendees) * 100).toFixed(1)}%`
        ]);

        autoTable(doc, {
            startY: yPosition,
            head: [['Ticket Type', 'Quantity', 'Percentage']],
            body: ticketData,
            theme: 'grid',
            headStyles: {
                fillColor: primaryColor,
                textColor: 255,
                fontStyle: 'bold'
            },
            bodyStyles: {
                textColor: textColor
            },
            alternateRowStyles: {
                fillColor: lightGray
            },
            margin: { left: 20, right: 20 }
        });

        // Generate filename if not provided
        const defaultFilename = `event-summary-${eventData.title.replace(/[^a-zA-Z0-9]/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
        const finalFilename = filename || defaultFilename;

        // Save the PDF
        doc.save(finalFilename);

    } catch (error) {
        console.error('Error generating summary PDF:', error);
        throw new Error('Failed to generate summary PDF export');
    }
}
